/**
 * Decision 0149: runner resources. Pure helpers shared by the hub (load tracking, the Overloaded state, the placement
 * penalty), the cloud's texts (list_runners, the turn file, read_run), the web (run view, runner pages) and the runner
 * (the machine line in a run's instructions).
 *
 * Load is information, never a gate: the Overloaded state is a label, placement only subtracts points, and nothing here
 * refuses a run. Exclusive keys and capacity slots stay the only hard limits.
 */
import type { LoadPressure, ProfileContainer, RunnerLoad, RunResources } from "@minga/protocol"
import { CONTAINER_INSTANCES, CONTAINER_PRICES, containerRateMicroUsdPerHour } from "./pricing"

// #region load tracking (hub)
/** A heartbeat load older than this is not "now" any more (the runner heartbeats every 10 s). */
export const LOAD_STALE_MS = 60_000
/** Hot this long without a break = Overloaded. */
export const OVERLOAD_SUSTAIN_MS = 5 * 60_000
/** Critical memory pressure this long = Overloaded. */
export const CRITICAL_SUSTAIN_MS = 2 * 60_000
/** Recent peaks cover this window (one bucket per minute, in the hub's memory only). */
export const PEAK_WINDOW_MS = 60 * 60_000
/** A gap between heartbeats longer than this breaks a hot streak. */
const STREAK_GAP_MS = 45_000

export type LoadState = "normal" | "busy" | "overloaded"
export type HotWhy = "memory" | "cpu" | "throttled"

export interface LoadBucket {
  readonly minute: number
  readonly cpuPct: number
  readonly memPct: number
  readonly pressure: LoadPressure
  readonly throttledPct?: number
}

/** The hub's memory of one runner's load (never stored, never in the event log). */
export interface RunnerLoadTrack {
  readonly last: RunnerLoad
  /** Cloud clock when `last` arrived. */
  readonly receivedAt: number
  readonly buckets: ReadonlyArray<LoadBucket>
  readonly hotSince: number | null
  readonly hotWhy: HotWhy | null
  readonly criticalSince: number | null
}

const PRESSURE_RANK: Record<LoadPressure, number> = { normal: 0, warn: 1, critical: 2 }
export const worsePressure = (a: LoadPressure, b: LoadPressure): LoadPressure =>
  PRESSURE_RANK[a] >= PRESSURE_RANK[b] ? a : b

/**
 * Why a sample is hot, the worst first: critical memory pressure, throttling, a pinned CPU. Memory pressure `warn` is
 * only busy: a Mac in daily use sits at warn for hours (the kernel's level 2 with a full swap) and is not overloaded.
 */
export const hotWhyOf = (l: RunnerLoad): HotWhy | null =>
  l.pressure === "critical"
    ? "memory"
    : (l.throttledPct ?? 0) >= 50
      ? "throttled"
      : l.cpuPct >= 90 || (l.cpuWaitPct ?? 0) >= 50
        ? "cpu"
        : null

/**
 * Decision 0158 (10-22 review: an idle Mac read "busy … pressure warn" at 62 % memory): macOS sits at its `warn` level
 * for hours in daily use, so `warn` makes a runner busy only when its memory is mostly used too.
 */
const WARN_BUSY_MEM_PCT = 80

const busyNow = (l: RunnerLoad): boolean =>
  l.pressure === "critical" ||
  (l.pressure === "warn" && l.memPct >= WARN_BUSY_MEM_PCT) ||
  l.cpuPct >= 75 ||
  (l.throttledPct ?? 0) >= 25

/** Fold one heartbeat load into a runner's track (pure). */
export const trackLoad = (
  prev: RunnerLoadTrack | undefined,
  load: RunnerLoad,
  now: number,
): RunnerLoadTrack => {
  const continuous = prev !== undefined && now - prev.receivedAt <= STREAK_GAP_MS
  const minute = Math.floor(now / 60_000)
  const oldest = minute - Math.floor(PEAK_WINDOW_MS / 60_000) + 1
  const buckets = (prev?.buckets ?? []).filter((b) => b.minute >= oldest)
  const lastBucket = buckets[buckets.length - 1]
  const sample: LoadBucket = {
    minute,
    cpuPct: load.cpuPct,
    memPct: load.memPct,
    pressure: load.pressure,
    ...(load.throttledPct !== undefined ? { throttledPct: load.throttledPct } : {}),
  }
  const merged: LoadBucket[] =
    lastBucket && lastBucket.minute === minute
      ? [
          ...buckets.slice(0, -1),
          {
            minute,
            cpuPct: Math.max(lastBucket.cpuPct, sample.cpuPct),
            memPct: Math.max(lastBucket.memPct, sample.memPct),
            pressure: worsePressure(lastBucket.pressure, sample.pressure),
            ...(lastBucket.throttledPct !== undefined || sample.throttledPct !== undefined
              ? { throttledPct: Math.max(lastBucket.throttledPct ?? 0, sample.throttledPct ?? 0) }
              : {}),
          },
        ]
      : [...buckets, sample]
  const why = hotWhyOf(load)
  const critical = load.pressure === "critical"
  return {
    last: load,
    receivedAt: now,
    buckets: merged,
    hotSince: why ? (continuous && prev?.hotSince != null ? prev.hotSince : now) : null,
    hotWhy: why,
    criticalSince: critical ? (continuous && prev?.criticalSince != null ? prev.criticalSince : now) : null,
  }
}

/** What list_runners, the turn file and the runner pages show about a runner's load. */
export interface RunnerLoadSummary {
  readonly state: LoadState
  /** Why it is Overloaded ("memory pressure critical for 6 min"); null otherwise. */
  readonly reason: string | null
  /** When the current hot streak started (Overloaded and busy). */
  readonly since: number | null
  /** Cloud clock of the newest sample. */
  readonly at: number
  readonly cores: number
  readonly vcpu?: number
  readonly memMb: number
  readonly cpuPct: number
  readonly memPct: number
  readonly pressure: LoadPressure
  readonly swapUsedMb?: number
  readonly throttledPct?: number
  readonly load1?: number
  readonly runs?: RunnerLoad["runs"]
  /** The worst minute of the last hour (the hub's memory; empty after a restart). */
  readonly peaks: {
    readonly windowMin: number
    readonly cpuPct: number
    readonly memPct: number
    readonly pressure: LoadPressure
    readonly throttledPct?: number
  }
}

const mins = (ms: number): number => Math.max(1, Math.round(ms / 60_000))

/** A runner's load as a state, or null when it reported none recently (older runner, offline, stale). */
export const loadSummary = (t: RunnerLoadTrack | undefined, now: number): RunnerLoadSummary | null => {
  if (!t || now - t.receivedAt > LOAD_STALE_MS) return null
  const l = t.last
  const criticalFor = t.criticalSince !== null ? now - t.criticalSince : 0
  const hotFor = t.hotSince !== null ? now - t.hotSince : 0
  const overloaded =
    (t.criticalSince !== null && criticalFor >= CRITICAL_SUSTAIN_MS) ||
    (t.hotSince !== null && hotFor >= OVERLOAD_SUSTAIN_MS)
  const reason = !overloaded
    ? null
    : t.criticalSince !== null && criticalFor >= CRITICAL_SUSTAIN_MS
      ? `memory nearly full for ${mins(criticalFor)} min`
      : t.hotWhy === "throttled"
        ? `CPU throttled ${Math.round(l.throttledPct ?? 0)}% for ${mins(hotFor)} min`
        : `CPU at ${Math.round(l.cpuPct)}% for ${mins(hotFor)} min`
  const windowMin =
    t.buckets.length > 0 ? Math.max(1, (t.buckets.at(-1)?.minute ?? 0) - (t.buckets[0]?.minute ?? 0) + 1) : 1
  const throttles = t.buckets.flatMap((b) => (b.throttledPct !== undefined ? [b.throttledPct] : []))
  return {
    state: overloaded ? "overloaded" : busyNow(l) ? "busy" : "normal",
    reason,
    since: overloaded ? (t.criticalSince ?? t.hotSince) : t.hotSince,
    at: t.receivedAt,
    cores: l.cores,
    ...(l.vcpu !== undefined ? { vcpu: l.vcpu } : {}),
    memMb: l.memMb,
    cpuPct: l.cpuPct,
    memPct: l.memPct,
    pressure: l.pressure,
    ...(l.swapUsedMb !== undefined ? { swapUsedMb: l.swapUsedMb } : {}),
    ...(l.throttledPct !== undefined ? { throttledPct: l.throttledPct } : {}),
    ...(l.load1 !== undefined ? { load1: l.load1 } : {}),
    ...(l.runs && l.runs.length > 0 ? { runs: l.runs } : {}),
    peaks: {
      windowMin,
      cpuPct: Math.max(l.cpuPct, ...t.buckets.map((b) => b.cpuPct)),
      memPct: Math.max(l.memPct, ...t.buckets.map((b) => b.memPct)),
      pressure: t.buckets.reduce<LoadPressure>((p, b) => worsePressure(p, b.pressure), l.pressure),
      ...(throttles.length > 0 ? { throttledPct: Math.max(...throttles) } : {}),
    },
  }
}

/** Placement points a loaded runner loses (decision 0149): a soft preference, never a refusal. */
export const LOAD_PENALTY = { busy: 15, overloaded: 40 } as const

/** How many points placement subtracts for a runner's load now; 0 without a recent load. */
export const loadPenalty = (s: Pick<RunnerLoadSummary, "state"> | null | undefined): number =>
  s?.state === "overloaded" ? LOAD_PENALTY.overloaded : s?.state === "busy" ? LOAD_PENALTY.busy : 0
// #endregion load tracking

// #region texts
const round = (n: number): number => Math.round(n)

/**
 * Decision 0158: the machine's memory pressure in plain words ("memory tight", "memory nearly full"); null when normal.
 * People read "pressure warn" as an alarm.
 */
export const pressureWords = (p: LoadPressure): string | null =>
  p === "critical" ? "memory nearly full" : p === "warn" ? "memory tight" : null
const gb = (mb: number): string => {
  const g = mb / 1024
  return g >= 10 ? String(Math.round(g)) : String(Math.round(g * 10) / 10)
}

/** "½", "2", "0.75". */
export const vcpuText = (v: number): string =>
  v === 0.5 ? "½" : v === 0.25 ? "¼" : String(Math.round(v * 100) / 100)

/** "½ vCPU", "2 vCPU" (a quota is known), "10 cores" (the OS's count). */
export const cpuSizeText = (o: { readonly cores: number; readonly vcpu?: number | undefined }): string =>
  o.vcpu !== undefined ? `${vcpuText(o.vcpu)} vCPU` : `${round(o.cores)} ${o.cores === 1 ? "core" : "cores"}`

/** The sentence a run's resources become (run view, read_run): "peak CPU 390% of 4 vCPU · throttled 38% · …". */
export const runResourcesText = (r: RunResources): string => {
  const parts: string[] = []
  parts.push(`peak CPU ${round(r.cpuPeakPct)}% of ${cpuSizeText(r)} (avg ${round(r.cpuAvgPct)}%)`)
  if (r.throttledPct !== undefined && r.throttledPct >= 1) parts.push(`throttled ${round(r.throttledPct)}%`)
  parts.push(`peak memory ${gb(r.rssPeakMb)} of ${gb(r.memMb)} GB`)
  if (r.oomKills !== undefined && r.oomKills > 0)
    parts.push(`${r.oomKills} out-of-memory kill${r.oomKills === 1 ? "" : "s"}`)
  const machine = [
    `CPU ${round(r.machineCpuPeakPct)}% peak, ${round(r.machineCpuAvgPct)}% avg`,
    `memory ${round(r.machineMemPeakPct)}%`,
    ...(r.pressurePeak !== "normal" ? [pressureWords(r.pressurePeak) ?? r.pressurePeak] : []),
    ...(r.swapPeakMb !== undefined && r.swapPeakMb >= 1024 ? [`swap ${gb(r.swapPeakMb)} GB`] : []),
  ]
  parts.push(`machine load ${machine.join(", ")}`)
  if (r.sharedCpuPeakPct !== undefined && r.sharedCpuPeakPct >= 1)
    parts.push(`Simulator/Xcode services peak ${round(r.sharedCpuPeakPct)}%`)
  return parts.join(" · ")
}

/**
 * Why a run's resources deserve a warning (the run view's chip), or null. The run hit its machine's limit: the quota
 * pinned most of the run, throttling, critical memory pressure or an OOM kill.
 */
export const runResourcesWarning = (r: RunResources): string | null => {
  if ((r.oomKills ?? 0) > 0) return "out of memory"
  if (r.pressurePeak === "critical") return "memory nearly full"
  if ((r.throttledPct ?? 0) >= 30) return "CPU throttled"
  if (r.machineCpuAvgPct >= 85) return "CPU pinned"
  return null
}

/** "CPU 45% · memory 62%" plus pressure and throttling when they matter; Overloaded leads with its reason. */
export const loadText = (s: RunnerLoadSummary): string => {
  const parts = [
    `CPU ${round(s.cpuPct)}% of ${cpuSizeText(s)}`,
    `memory ${round(s.memPct)}% of ${gb(s.memMb)} GB`,
    ...(s.pressure !== "normal" ? [pressureWords(s.pressure) ?? s.pressure] : []),
    ...(s.throttledPct !== undefined && s.throttledPct >= 1 ? [`throttled ${round(s.throttledPct)}%`] : []),
  ]
  const head = s.state === "overloaded" ? `overloaded (${s.reason ?? "sustained load"})` : s.state
  return `${head}: ${parts.join(", ")}`
}

/** The recent peaks in words: "peaks last 60 min: CPU 98%, memory 81%, memory tight". */
export const loadPeaksText = (s: RunnerLoadSummary): string =>
  `peaks last ${s.peaks.windowMin} min: CPU ${round(s.peaks.cpuPct)}%, memory ${round(s.peaks.memPct)}%${
    s.peaks.pressure !== "normal" ? `, ${pressureWords(s.peaks.pressure) ?? s.peaks.pressure}` : ""
  }${s.peaks.throttledPct !== undefined && s.peaks.throttledPct >= 1 ? `, throttled ${round(s.peaks.throttledPct)}%` : ""}`
// #endregion texts

// #region Platform profile sizes
const usd = (micro: number): number => Math.round(micro / 100) / 10_000

/** A Platform Runner profile's sandbox size and price (list prices, decision 0107's table), or null if unknown. */
export const profileContainerOf = (size: string): ProfileContainer | null => {
  const i = CONTAINER_INSTANCES[size]
  const rate = containerRateMicroUsdPerHour(size)
  if (!i || rate === null) return null
  const floor = (i.memoryMib / 1024) * CONTAINER_PRICES.memoryGibHour + i.diskGb * CONTAINER_PRICES.diskGbHour
  return {
    size,
    vcpu: i.vcpu,
    memoryMib: i.memoryMib,
    usdPerHour: usd(rate),
    usdPerHourFloor: usd(Math.ceil(floor)),
  }
}

/** "standard-3: 2 vCPU, 8 GiB, ≤ $0.22/h (memory and disk $0.076/h; CPU billed as used)". */
export const containerText = (c: ProfileContainer): string =>
  `${c.name && c.name !== c.size ? `"${c.name}" ` : ""}${c.size}: ${vcpuText(c.vcpu)} vCPU, ${gb(c.memoryMib)} GiB, ≤ $${c.usdPerHour}/h (memory and disk $${c.usdPerHourFloor}/h; CPU billed as used)`
// #endregion Platform profile sizes
